/* ==========================================================================
   NGA — Cart

   One controller for both the drawer and the cart page. Every mutation goes
   through /cart/change.js or /cart/add.js and then re-renders the affected
   sections through the Section Rendering API, so the free-shipping meter,
   the upsell rail and the totals can never disagree with the line items.
   ========================================================================== */
(function () {
  'use strict';

  var DRAWER_SECTION = 'nga-cart-drawer';
  var PAGE_SECTION = 'nga-cart-page';

  var NGA = (window.NGA = window.NGA || {});
  var routes = (window.routes = window.routes || {});
  var changeUrl = routes.cart_change_url || '/cart/change';
  var addUrl = routes.cart_add_url || '/cart/add';
  var updateUrl = routes.cart_update_url || '/cart/update';

  var busy = false;

  function drawer() { return document.querySelector('[data-nga-cart]'); }
  function scrim() { return document.querySelector('[data-nga-cart-scrim]'); }

  /* ---------------------------------------------------------------------
     Open / close
     --------------------------------------------------------------------- */
  var lastFocus = null;

  function open() {
    var d = drawer(), sc = scrim();
    if (!d) return;
    lastFocus = document.activeElement;
    if (sc) { sc.hidden = false; requestAnimationFrame(function () { sc.classList.add('is-open'); }); }
    d.classList.add('is-open');
    document.body.classList.add('nga-no-scroll');
    var close = d.querySelector('[data-nga-cart-close]');
    if (close) close.focus();
    document.dispatchEvent(new CustomEvent('nga:cart:open'));
  }

  function close() {
    var d = drawer(), sc = scrim();
    if (!d) return;
    d.classList.remove('is-open');
    if (sc) {
      sc.classList.remove('is-open');
      setTimeout(function () { sc.hidden = true; }, 400);
    }
    document.body.classList.remove('nga-no-scroll');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  NGA.cart = { open: open, close: close };

  /* ---------------------------------------------------------------------
     Section re-render
     --------------------------------------------------------------------- */
  function sectionsOnPage() {
    var list = [];
    if (document.querySelector('[data-nga-cart]')) list.push(DRAWER_SECTION);
    if (document.querySelector('[data-nga-cart-page]')) list.push(PAGE_SECTION);
    return list;
  }

  function swap(html, selector, sectionId) {
    var incoming = new DOMParser().parseFromString(html, 'text/html');
    var next = incoming.querySelector(selector);
    var current = document.querySelector(selector);
    if (!next || !current) return;
    current.replaceWith(next);
    if (sectionId) {
      document.dispatchEvent(new CustomEvent('nga:refresh', { detail: { section: sectionId } }));
    }
  }

  function refresh(sections) {
    var wanted = sections || sectionsOnPage();
    if (!wanted.length) return Promise.resolve();

    return fetch('?sections=' + wanted.join(','), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var wasOpen = drawer() && drawer().classList.contains('is-open');

        if (data[DRAWER_SECTION]) {
          // The scrim lives inside the drawer section, so replace the whole
          // rendered block rather than the aside alone.
          var host = document.getElementById('shopify-section-' + DRAWER_SECTION);
          if (host) {
            var doc = new DOMParser().parseFromString(data[DRAWER_SECTION], 'text/html');
            var incoming = doc.getElementById('shopify-section-' + DRAWER_SECTION) || doc.body;
            host.innerHTML = incoming.innerHTML;
          }
          if (wasOpen) {
            var d = drawer(), sc = scrim();
            if (d) d.classList.add('is-open');
            if (sc) { sc.hidden = false; sc.classList.add('is-open'); }
          }
        }

        if (data[PAGE_SECTION]) swap(data[PAGE_SECTION], '[data-nga-cart-page]', PAGE_SECTION);

        return fetch('/cart.js').then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        paintCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('nga:cart:change', { detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('nga:refresh'));
      })
      .catch(function () { /* leave the DOM as it stands */ });
  }

  function paintCount(n) {
    document.querySelectorAll('.nga-header__count, [data-nga-cart-count]').forEach(function (el) {
      el.textContent = n > 99 ? '99+' : String(n);
      el.hidden = n === 0;
    });
  }

  /* ---------------------------------------------------------------------
     Mutations
     --------------------------------------------------------------------- */
  function setBusy(on) {
    busy = on;
    var d = drawer();
    if (d) d.classList.toggle('is-busy', on);
    var page = document.querySelector('[data-nga-cart-page]');
    if (page) page.classList.toggle('is-busy', on);
  }

  function change(key, quantity) {
    if (busy) return Promise.resolve();
    setBusy(true);
    return fetch(changeUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .catch(function () {})
      .then(function () { setBusy(false); });
  }

  function add(variantId, quantity) {
    if (busy) return Promise.resolve();
    setBusy(true);
    return fetch(addUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity || 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function () { return refresh(); })
      .catch(function () {})
      .then(function () { setBusy(false); });
  }

  NGA.cart.add = add;
  NGA.cart.change = change;
  NGA.cart.refresh = refresh;

  function updateAttributes(body) {
    return fetch(updateUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).catch(function () {});
  }

  /* ---------------------------------------------------------------------
     Delegated events — the DOM is replaced wholesale, so nothing may bind
     to an individual node.
     --------------------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    // Open
    var opener = e.target.closest('[data-nga-cart-open], .nga-header__cart, #cart-icon-bubble');
    if (opener && drawer() && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
      e.preventDefault();
      open();
      return;
    }

    if (e.target.closest('[data-nga-cart-close]') || e.target.closest('[data-nga-cart-scrim]')) {
      e.preventDefault();
      close();
      return;
    }

    var qty = e.target.closest('[data-nga-cart-qty]');
    if (qty) {
      e.preventDefault();
      var row = qty.closest('[data-nga-cart-item]');
      var input = row && row.querySelector('[data-nga-cart-input]');
      if (!row || !input) return;
      var next = parseInt(input.value, 10) || 0;
      next += qty.getAttribute('data-nga-cart-qty') === 'up' ? 1 : -1;
      if (next < 0) next = 0;
      input.value = next;
      change(row.getAttribute('data-nga-cart-item'), next);
      return;
    }

    var rm = e.target.closest('[data-nga-cart-remove]');
    if (rm) {
      e.preventDefault();
      var line = rm.closest('[data-nga-cart-item]');
      if (line) change(line.getAttribute('data-nga-cart-item'), 0);
      return;
    }

    var addBtn = e.target.closest('[data-nga-cart-add]');
    if (addBtn) {
      e.preventDefault();
      var label = addBtn.textContent;
      addBtn.classList.add('is-added');
      addBtn.textContent = addBtn.getAttribute('data-added-label') || 'Added';
      add(addBtn.getAttribute('data-nga-cart-add'), 1).then(function () {
        // The section re-render replaces this button, so restoring the label
        // only matters when the fetch failed and the node survived.
        if (document.body.contains(addBtn)) {
          addBtn.classList.remove('is-added');
          addBtn.textContent = label;
        }
      });
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-nga-cart-input]');
    if (input) {
      var row = input.closest('[data-nga-cart-item]');
      if (!row) return;
      var v = parseInt(input.value, 10);
      if (isNaN(v) || v < 0) v = 0;
      change(row.getAttribute('data-nga-cart-item'), v);
      return;
    }

    var gift = e.target.closest('[data-nga-cart-gift]');
    if (gift) {
      updateAttributes({ attributes: { gift: gift.checked ? 'Yes' : '' } });
      return;
    }
  });

  var noteTimer;
  document.addEventListener('input', function (e) {
    var note = e.target.closest('[data-nga-cart-note]');
    if (!note) return;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(function () { updateAttributes({ note: note.value }); }, 600);
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var d = drawer();
    if (d && d.classList.contains('is-open')) close();
  });

  // Anything elsewhere in the theme that adds to the bag can ask for a refresh.
  document.addEventListener('nga:cart:added', function (e) {
    refresh().then(function () {
      if (!e.detail || e.detail.open !== false) open();
    });
  });
})();
